import { Alert, Box, Modal, Svg, Text } from '@hopskipdrive/react-components';
import { ChevronRight, Email, PhoneAndroidOutlined } from '@mui/icons-material';
import styled from 'styled-components';

import { colorPalette, colorTokens } from '../../../../shared/components/variables';
import { MFAMethod, MFAMethodOption } from '../../../types';

import { CloseButton, MFAModalPaper } from './MultiFactorAuth.styles';

interface Props {
  methods: MFAMethodOption[];
  onSelect: (method: MFAMethod) => void;
  onClose: () => void;
  sendingMethod: MFAMethod | null;
  errors: string[];
  methodCooldownSeconds?: Record<MFAMethod, number>;
}

const MethodButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid ${colorTokens.border.subtle};
  border-radius: 5px;
  background: ${colorPalette.neutral100};
  color: ${colorTokens.text.dark};
  text-align: left;
  font: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colorTokens.text.hover};
    outline-offset: 2px;
  }
  &:disabled {
    cursor: wait;
    opacity: 0.6;
  }
  &:hover:not(:disabled) {
    background: ${colorTokens.surface.neutral};
  }
`;

const MethodLabel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const MFAMethodSelection = ({
  methods,
  onSelect,
  onClose,
  sendingMethod,
  errors,
  methodCooldownSeconds = { email: 0, sms: 0 },
}: Props) => (
  <Modal
    open
    maxWidth="md"
    onClose={(_, reason) => {
      if (reason !== 'backdropClick') onClose();
    }}
    disableRestoreFocus={false}
    aria-labelledby="mfa-method-title"
    aria-describedby="mfa-method-description"
    PaperComponent={MFAModalPaper}
    PaperProps={{ style: { borderRadius: 8 } }}
    data-testid="mfa-method-modal"
  >
    <Modal.Title id="mfa-method-title" display="flex" alignItems="center">
      Multi Factor Authentication
      <CloseButton type="button" aria-label="Close verification" onClick={onClose} data-testid="mfa-method-close">
        <Svg height="20px" icon="close" />
      </CloseButton>
    </Modal.Title>
    <Modal.Content>
      <Text id="mfa-method-description" variant="bodyS">
        Choose how you&apos;d like to receive your verification code.
      </Text>
      <Box display="flex" flexDirection="column" gap="8px" mt="16px" aria-busy={!!sendingMethod}>
        {methods.map(({ method, destination }, index) => (
          <MethodButton
            key={method}
            type="button"
            autoFocus={index === 0}
            disabled={!!sendingMethod || methodCooldownSeconds[method] > 0}
            onClick={() => onSelect(method)}
            data-testid={`mfa-method-${method}`}
          >
            {method === 'email' ? (
              <Email aria-hidden="true" fontSize="small" sx={{ color: colorTokens.text.active }} />
            ) : (
              <PhoneAndroidOutlined aria-hidden="true" fontSize="small" sx={{ color: colorTokens.text.medium }} />
            )}
            <MethodLabel>
              <Text variant="labelM">{method === 'email' ? 'Email' : 'SMS'}</Text>
              <Text variant="bodyS" textColor="secondary">
                {destination}
              </Text>
            </MethodLabel>
            <ChevronRight aria-hidden="true" fontSize="small" />
          </MethodButton>
        ))}
      </Box>
      {sendingMethod && (
        <Box role="status" mt="12px">
          <Text variant="bodyS">Sending code by {sendingMethod === 'sms' ? 'SMS' : 'email'}…</Text>
        </Box>
      )}
      {methods.some(({ method }) => methodCooldownSeconds[method] > 0) && (
        <Text role="status" variant="bodyS" mt="12px">
          {methods.every(({ method }) => methodCooldownSeconds[method] > 0)
            ? 'Please wait before requesting another code.'
            : 'Choose another method, or wait to request a new code with the same method.'}
        </Text>
      )}
      {!!errors.length && (
        <Box mt="12px">
          {errors.map((error) => (
            <Alert key={error} severity="error" message={error} />
          ))}
        </Box>
      )}
    </Modal.Content>
  </Modal>
);
